import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getOrCreateAdvisorPhaseMeeting, getSchools } from '../../API/apiService';
import { constants } from '../../config/constants';
import './style.scss';

const COP_PHASES = [
  { id: 'solicitud', label: 'Solicitud' },
  { id: 'planeacion', label: 'Planeacion' },
  { id: 'desarrollo', label: 'Desarrollo' },
  { id: 'cierre', label: 'Cierre' }
];

const COP_MEETING_ORDERS = [1, 2, 3, 4];

function resolveAdvisorName(school) {
  return school?.name ?? school?.address ?? school?.code ?? 'Asesoria';
}

function getAdvisorUserId(school, fallbackUserId) {
  return Number(
    school?.userId ??
      school?.user_id ??
      school?.user?.id ??
      fallbackUserId
  );
}

function CopAdvisorEvidences() {
  const location = useLocation();
  const navigate = useNavigate();
  const { schoolParam } = useParams();
  const receivedSchool = location.state?.school;
  const currentUserId = localStorage.getItem(constants.USER_ID);
  const [advisorSchool, setAdvisorSchool] = useState(receivedSchool || null);
  const [isLoadingSchool, setIsLoadingSchool] = useState(!receivedSchool);
  const [loadingMeetingKey, setLoadingMeetingKey] = useState('');

  const schoolId =
    advisorSchool?.id ?? receivedSchool?.id ?? (schoolParam ? Number(schoolParam) : null);
  const currentBackTo = useMemo(
    () => ({
      pathname: location.pathname,
      state: location.state
    }),
    [location.pathname, location.state]
  );
  const advisorTitle = advisorSchool?.code || receivedSchool?.code || 'Asesoria';
  const advisorName = resolveAdvisorName(advisorSchool || receivedSchool);

  useEffect(() => {
    if (receivedSchool || !schoolParam) {
      setIsLoadingSchool(false);
      return;
    }

    let isMounted = true;

    async function loadAdvisorSchool() {
      setIsLoadingSchool(true);
      const schoolsResponse = await getSchools(
        currentUserId && currentUserId !== 'null' ? Number(currentUserId) : null
      );
      const schools = Array.isArray(schoolsResponse)
        ? schoolsResponse
        : Array.isArray(schoolsResponse?.data)
          ? schoolsResponse.data
          : [];
      const foundSchool = schools.find(
        school => Number(school?.id) === Number(schoolParam)
      );

      if (!isMounted) return;

      setAdvisorSchool(foundSchool || { id: Number(schoolParam) });
      setIsLoadingSchool(false);
    }

    loadAdvisorSchool();

    return () => {
      isMounted = false;
    };
  }, [currentUserId, receivedSchool, schoolParam]);

  async function handleOpenMeeting(phase, order) {
    if (!schoolId || !phase || !order || loadingMeetingKey) return;

    const loadingKey = `${phase}-${order}-edit`;
    setLoadingMeetingKey(loadingKey);

    try {
      const school = advisorSchool || receivedSchool || { id: schoolId };
      const meeting = await getOrCreateAdvisorPhaseMeeting({
        schoolId: Number(schoolId),
        phase,
        order: Number(order),
        userId: getAdvisorUserId(school, currentUserId)
      });
      const meetingGroupId = meeting?.meetingGroupId ?? meeting?.id;

      if (!meetingGroupId) return;

      const params = new URLSearchParams({
        encuentro: String(meetingGroupId),
        phase,
        order: String(order)
      });

      navigate(`/escuelas/grupo/lista_de_encuentros/encuentro?${params.toString()}`, {
        state: {
          backTo: currentBackTo,
          school,
          advisorPhase: phase,
          advisorMeetingOrder: Number(order)
        }
      });
    } finally {
      setLoadingMeetingKey('');
    }
  }

  async function handleOpenAttendanceList(phase, order) {
    if (!schoolId || !phase || !order || loadingMeetingKey) return;

    const loadingKey = `${phase}-${order}-attendance`;
    setLoadingMeetingKey(loadingKey);
    const school = advisorSchool || receivedSchool || { id: schoolId };

    try {
      const meeting = await getOrCreateAdvisorPhaseMeeting({
        schoolId: Number(schoolId),
        phase,
        order: Number(order),
        userId: getAdvisorUserId(school, currentUserId)
      });
      const meetingGroupId = meeting?.meetingGroupId ?? meeting?.id;
      if (!meetingGroupId) return;

      navigate(`/generar-listado-asistencia?evento_id=${meetingGroupId}`, {
        state: {backTo: currentBackTo, school}
      });
    } finally {
      setLoadingMeetingKey('');
    }
  }

  return (
    <section className='cop-advisor-evidences'>
      <div className='cop-advisor-evidences__main group'>
        <div className='group__info info'>
          <div className='info__icon icon'>
            <span className='material-symbols-rounded icon__img'>folder_open</span>
          </div>
          <span className='info__title'>{advisorTitle}</span>
          <span className='info__value'>
            {isLoadingSchool ? 'Cargando asesoria...' : advisorName}
          </span>
        </div>
      </div>

      <div className='cop-advisor-evidences__phases'>
        {COP_PHASES.map(phase => (
          <section className='cop-phase-card' key={phase.id}>
            <div className='cop-phase-card__header'>
              <p className='cop-phase-card__title'>{phase.label}</p>
            </div>

            <div className='cop-phase-card__meetings'>
              {COP_MEETING_ORDERS.map(order => {
                const editLoadingKey = `${phase.id}-${order}-edit`;
                const attendanceLoadingKey = `${phase.id}-${order}-attendance`;
                const isLoading = loadingMeetingKey === editLoadingKey;
                const isAttendanceLoading = loadingMeetingKey === attendanceLoadingKey;
                const showsAttendance = phase.id === 'planeacion' || phase.id === 'desarrollo';

                return (
                  <div className='cop-phase-card__meeting-wrapper' key={`${phase.id}-${order}`}>
                    <button
                      type='button'
                      className='cop-phase-card__meeting'
                      onClick={() => handleOpenMeeting(phase.id, order)}
                      disabled={Boolean(loadingMeetingKey) || isLoadingSchool}
                    >
                      <span className='cop-phase-card__meeting-text'>
                        {isLoading ? 'Abriendo...' : `Encuentro ${order}`}
                      </span>
                      <span className='material-symbols-rounded cop-phase-card__meeting-icon'
                            aria-hidden='true'>edit_square</span>
                    </button>
                    {showsAttendance && (
                      <button
                        type='button'
                        className='cop-phase-card__attendance'
                        onClick={() => handleOpenAttendanceList(phase.id, order)}
                        disabled={Boolean(loadingMeetingKey) || isLoadingSchool}
                      >
                        <span className='material-symbols-rounded' aria-hidden='true'>fact_check</span>
                        <span>{isAttendanceLoading ? 'Abriendo...' : 'Ver asistencias'}</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}

export default CopAdvisorEvidences;
